import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Brain, Loader2, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  MmObject,
  MmObjectType,
  MmProfile,
  aiTargetState,
  createObject,
  inferProfile,
  listObjects,
  snapshotProfile,
} from "../../api/mentalModels";
import { getStoredRegister, useMmText } from "../../hooks/useMmText";
import type { MmRegister } from "../../api/mentalModels";
import MmProfilePanel from "./MmProfilePanel";
import MmPathPicker from "./MmPathPicker";
import MmPathEditor from "./MmPathEditor";
import { MmPath, createTemplate, getMastery, type MmOperatorMastery } from "../../api/mentalModels";
import MmMasteryPanel from "./MmMasteryPanel";
import MmRealityShaping from "./MmRealityShaping";
import MmListSections from "./MmListSections";
import MmRow from "./MmRow";
import type { MmInitialVisit } from "./MmModelPage";
import { mmTypeStyle } from "../../lib/mmTypeStyle";
import { useSpaces } from "../../context/SpaceProvider";

/**
 * "Your skills & patterns" — the full studio of the Mental Model Engine
 * (design §10.5), reached from the Models tab header. It keeps capturing a new
 * model, the mastery panel, reality shaping and the list; every model opens on
 * its page in the Models tab (MmModelPage), where visits happen.
 */

const OBJECT_TYPES: MmObjectType[] = [
  "belief", "fear", "goal", "decision", "self_image", "value", "memory", "other",
];

type View = { name: "list" } | { name: "capture" };

interface Props {
  onCountChange?: (count: number) => void;
  /** Open a model on its page (Models tab), optionally straight into a visit. */
  onOpenModel: (id: string, initialVisit?: MmInitialVisit) => void;
  /** Back to the Models tab without a model selected. */
  onBack?: () => void;
}

const MmStudio: React.FC<Props> = ({ onCountChange, onOpenModel, onBack }) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const [register] = useState<MmRegister>(getStoredRegister());
  const { tm } = useMmText(register);
  const { spaces } = useSpaces();

  const [view, setView] = useState<View>({ name: "list" });
  const [objects, setObjects] = useState<MmObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // ICDF §4 — operator mastery (derived server-side from mm_events)
  const [mastery, setMastery] = useState<MmOperatorMastery[] | null>(null);
  const [reasonMix, setReasonMix] = useState<{ friction: number; plan: number }>({
    friction: 0,
    plan: 0,
  });
  useEffect(() => {
    getMastery(retryFetch)
      .then((res) => {
        setMastery(res.operators);
        setReasonMix(res.reasonMix);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture state
  const [statement, setStatement] = useState("");
  const [objectType, setObjectType] = useState<MmObjectType>("other");
  const [aiProfile, setAiProfile] = useState<MmProfile | null>(null);
  const [aiConfidence, setAiConfidence] = useState<Record<string, number>>({});
  const [profileDraft, setProfileDraft] = useState<MmProfile>({});
  const [distressPaused, setDistressPaused] = useState(false);
  const [pendingObject, setPendingObject] = useState<MmObject | null>(null);

  // Goal-directed mode (capture step 2)
  const [statedOutcome, setStatedOutcome] = useState("");
  // Predefined path selection (capture step)
  const [chosenPath, setChosenPath] = useState<MmPath | null>(null);
  // Build a reusable path (opens the path editor)
  const [buildingPath, setBuildingPath] = useState(false);
  // Bumped after template changes so path pickers remount with fresh data
  const [pathsVersion, setPathsVersion] = useState(0);

  const language = i18n.language?.split("-")[0] || "en";

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listObjects(retryFetch, { sort: "attention" });
      setObjects(res.objects);
      onCountChange?.(res.objects.filter((o) => o.status === "active").length);
    } catch {
      /* list stays as-is */
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ---- capture flow ---------------------------------------------------------

  const startCapture = () => {
    setStatement("");
    setStatedOutcome("");
    setChosenPath(null);
    setBuildingPath(false);
    setObjectType("other");
    setAiProfile(null);
    setAiConfidence({});
    setProfileDraft({});
    setDistressPaused(false);
    setPendingObject(null);
    setView({ name: "capture" });
  };

  const captureAndInfer = async () => {
    if (statement.trim().length < 3) return;
    setBusy(true);
    try {
      const created = await createObject(retryFetch, { statement: statement.trim(), objectType });
      setPendingObject(created.object);
      setProfileDraft(created.object.profile);
      try {
        const inferred = await inferProfile(retryFetch, {
          statement: statement.trim(),
          objectType,
          language,
        });
        if (inferred.distress) {
          setDistressPaused(true);
          return;
        }
        if (inferred.profile) {
          setAiProfile(inferred.profile);
          setAiConfidence(inferred.confidence || {});
          setProfileDraft({ ...created.object.profile, ...inferred.profile });
        }
      } catch {
        /* inference is optional — user rates manually */
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  /** Save the rating, then open the new model's page straight into a visit. */
  const beginVisit = async () => {
    if (!pendingObject) return;
    setBusy(true);
    try {
      // Persist the (possibly corrected) profile before opening the visit
      const sources: Record<string, "user" | "ai"> = {};
      Object.keys(profileDraft).forEach((attr) => {
        const aiVal = aiProfile ? (aiProfile as any)[attr] : undefined;
        sources[attr] = aiVal !== undefined && (profileDraft as any)[attr] === aiVal ? "ai" : "user";
      });
      const saved = await snapshotProfile(retryFetch, pendingObject._id, {
        profile: profileDraft,
        profileSource: sources,
        trigger: "capture_rating",
      });
      // Goal-directed mode: translate the stated outcome into a target state
      let targetState: MmProfile | undefined;
      if (statedOutcome.trim()) {
        try {
          const ts = await aiTargetState(retryFetch, {
            statedOutcome: statedOutcome.trim(),
            fiObject: saved.object._id,
            language,
            register,
          });
          targetState = ts.targetState;
        } catch {
          /* goal mode is optional */
        }
      }
      onOpenModel(saved.object._id, {
        ask: false,
        goal: {
          statedOutcome: statedOutcome.trim() || undefined,
          targetState,
          pathId: chosenPath?.id,
        },
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // ---- renders ---------------------------------------------------------------

  if (view.name === "capture") {
    return (
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => setView({ name: "list" })}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back", "Back")}
        </button>

        {distressPaused ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900 space-y-2">
            <p className="font-medium">{tm("distress_title", "Let's pause here.")}</p>
            <p>
              {tm(
                "distress_body",
                "What you wrote sounds like a heavy moment. This tool isn't the right support for acute distress — please reach out to someone you trust or a local support line. Your note was saved privately and nothing else happens with it."
              )}
            </p>
            <Button variant="outline" onClick={() => setView({ name: "list" })}>
              {t("back", "Back")}
            </Button>
          </div>
        ) : !pendingObject ? (
          <div className="space-y-5">
            <div className="text-center pt-2">
              <span className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-primary/[0.07] mb-3">
                <Brain className="w-5 h-5 text-primary/70" />
              </span>
              <h3 className="text-lg font-semibold tracking-tight">
                {tm("capture_title", "What's the thought that's bothering you?")}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
                {tm("capture_help", "Write it exactly as it plays in your head — verbatim, unpolished.")}
              </p>
            </div>
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={4}
              autoFocus
              placeholder={tm("capture_placeholder", "e.g. I'm too old to switch fields")}
              className={cn(
                "w-full rounded-2xl border border-border bg-white px-4 py-3.5 text-base leading-relaxed shadow-sm",
                "transition-shadow duration-150 placeholder:text-muted-foreground/60",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary/40"
              )}
            />
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                {tm("capture_type_label", "It feels like a…")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {OBJECT_TYPES.map((type) => {
                  const v = mmTypeStyle(type);
                  const Icon = v.icon;
                  const selected = objectType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setObjectType(type)}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs min-h-[32px] cursor-pointer",
                        "transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        selected
                          ? "border-primary/50 bg-primary/[0.08] text-primary font-medium shadow-sm"
                          : "border-border/80 bg-white text-muted-foreground hover:border-primary/25 hover:text-foreground"
                      )}
                    >
                      <Icon className={cn("w-3.5 h-3.5", selected ? "text-primary" : v.text)} />
                      {tm(`type_${type}`, type.replace("_", " "))}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button
              size="lg"
              className="w-full sm:w-auto"
              onClick={captureAndInfer}
              disabled={busy || statement.trim().length < 3}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("capture_next", "Capture it")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl bg-gradient-to-br from-primary/[0.05] via-muted/40 to-transparent border border-border/50 px-5 py-4">
              <p className="text-base leading-relaxed text-foreground/90 italic">
                “{pendingObject.statement}”
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-white p-4 sm:p-5">
              <h3 className="text-sm font-semibold mb-4">
                {tm("rate_title", "How does it sit with you right now?")}
              </h3>
              <MmProfilePanel
                profile={profileDraft}
                profileSource={pendingObject.profileSource}
                confidence={aiProfile ? aiConfidence : undefined}
                register={register}
                objectType={pendingObject.objectType}
                onChange={(attr, value) => setProfileDraft({ ...profileDraft, [attr]: value })}
              />
            </div>
            <div className="rounded-2xl border border-border/70 bg-white p-4 sm:p-5 space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {tm("goal_label", "What would you like to be different? (optional)")}
                </label>
                <input
                  value={statedOutcome}
                  onChange={(e) => setStatedOutcome(e.target.value)}
                  placeholder={tm("goal_placeholder", "e.g. stop dreading it and decide whether to do it")}
                  className="mt-1.5 w-full rounded-lg border border-border px-3 py-2 text-sm bg-background transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                />
              </div>
              <div>
              <label className="text-sm font-medium">
                {tm("path_pick_label", "…or follow a proven path (optional)")}
              </label>
              {chosenPath ? (
                <div className="mt-1 flex items-center gap-2 text-sm">
                  <span className="rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium">
                    {chosenPath.kind === "template"
                      ? chosenPath.name
                      : tm(`script_${chosenPath.id}_name`, chosenPath.id)}
                  </span>
                  <button
                    onClick={() => setChosenPath(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t("cancel", "Cancel")}
                  </button>
                </div>
              ) : buildingPath ? (
                <div className="mt-1.5">
                  <MmPathEditor
                    register={register}
                    busy={busy}
                    onCancel={() => setBuildingPath(false)}
                    onSave={async (name, steps) => {
                      setBusy(true);
                      try {
                        await createTemplate(retryFetch, {
                          name,
                          operatorIds: steps,
                          objectTypes: objectType !== "other" ? [objectType] : [],
                        });
                        setBuildingPath(false);
                        setPathsVersion((v) => v + 1);
                      } catch (e) {
                        console.error(e);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="mt-1">
                  <MmPathPicker
                    key={pathsVersion}
                    objectType={objectType}
                    register={register}
                    onPick={(p) => setChosenPath(p)}
                    onCreateNew={() => setBuildingPath(true)}
                    compact
                  />
                </div>
              )}
            </div>
            </div>
            <Button size="lg" className="w-full sm:w-auto" onClick={beginVisit} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("start_working", "Start working on it")}
            </Button>
          </div>
        )}
      </div>
    );
  }


  // list view
  return (
    <div className="max-w-2xl mx-auto">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 min-h-[44px] text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {tm("studio_back", "Your mental models")}
        </button>
      )}
      <div className="mb-4">
        <h2 className="text-xl font-semibold tracking-tight">
          {tm("skills_title", "Your skills & patterns")}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {tm("studio_subtitle", "Beliefs, fears and goals you're deliberately reworking.")}
        </p>
      </div>

      <Button onClick={startCapture} className="mb-4">
        <Plus className="w-4 h-4 me-1.5" />
        {tm("new_object", "Work on a thought")}
      </Button>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("loading", "Loading…")}
        </div>
      ) : objects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-gradient-to-b from-muted/30 to-transparent px-8 py-12 text-center text-sm text-muted-foreground">
          <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/[0.07] mb-3">
            <Brain className="w-6 h-6 text-primary/60" />
          </span>
          <p className="max-w-xs mx-auto leading-relaxed">
            {tm(
              "empty_state",
              "Nothing here yet. Bring the sentence that keeps looping in your head."
            )}
          </p>
        </div>
      ) : (
        <MmListSections
          objects={objects}
          spaces={spaces}
          tm={tm}
          onOpen={(o) => onOpenModel(o._id)}
          renderRow={(o) => <MmRow object={o} onClick={() => onOpenModel(o._id)} />}
        />
      )}

      {/* ICDF §4 — your operators, as a growing skill constellation */}
      <MmMasteryPanel mastery={mastery} reasonMix={reasonMix} register={register} />

      {/* ICDF §5.2 — Reality-Shaping: what the period's experiences keep asking */}
      <MmRealityShaping
        register={register}
        onDesign={(text) => {
          startCapture();
          setStatement(text);
          setObjectType("goal");
        }}
      />
    </div>
  );
};

export default MmStudio;
